import { axiosDefaultInstance } from '@/http/default'

import type Employee from '@/model/Employee'
import { Role, type EmployeeEmailUpdateRequest, type EmployeeRequest } from '@/model/Employee'
import type Page from '@/model/Page'
import { useProfileStore } from '@/store/profile.module'
import * as Sentry from '@sentry/vue'
import type { AxiosInstance } from 'axios'
import handleError from './util/ErrorHandler'
import type { ServiceError } from './util/ServiceError'

class EmployeeService {
  private axiosInstance: AxiosInstance
  private storeProfile: any

  constructor() {
    this.axiosInstance = axiosDefaultInstance
    // this.storeProfile = useProfileStore()
  }

  async findMe(force = false): Promise<Employee | ServiceError> {
    this.storeProfile = useProfileStore()

    if (!force && this.storeProfile.profile) {
      return this.storeProfile.profile
    }

    try {
      const networkResponse = await this.axiosInstance.get<Employee>(
        `${import.meta.env.VITE_API_BASE_URL}/employee/me`
      )

      this.storeProfile.saveProfile(networkResponse.data)

      Sentry.setUser({
        id: networkResponse.data.id,
        email: networkResponse.data.email
      })

      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async findAll(): Promise<Employee[] | ServiceError> {
    this.storeProfile = useProfileStore()

    const params: string[][] = []
    const searchParams = new URLSearchParams(params)

    const companyId: string = this.storeProfile.selectedCompany.id

    try {
      const networkResponse = await this.axiosInstance.get<Employee[]>(
        `${import.meta.env.VITE_API_BASE_URL}/company/${companyId}/employee`,
        { params: searchParams }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async findAllV2(
    pageNumber?: number,
    pageSize?: number,
    name?: string,
    surname?: string,
    email?: string,
    nif?: string,
    phoneNumber?: string,
    role?: string,
    showInactive?: boolean,
    sort?: string,
    direction?: string
  ): Promise<Page<Employee> | ServiceError> {
    this.storeProfile = useProfileStore()

    const params: string[][] = []

    if (pageSize !== undefined) {
      params.push(['pageSize', pageSize.toString()])
    }
    if (pageNumber !== undefined) {
      params.push(['pageNumber', pageNumber.toString()])
    }
    if (name) {
      params.push(['name', name])
    }
    if (surname) {
      params.push(['surname', surname])
    }
    if (email) {
      params.push(['email', email])
    }
    if (nif) {
      params.push(['nif', nif])
    }
    if (phoneNumber) {
      params.push(['phoneNumber', phoneNumber])
    }
    if (role) {
      params.push(['role', role])
    }
    if (showInactive !== undefined) {
      params.push(['showInactive', showInactive.toString()])
    }

    if (sort) {
      params.push(['sort', sort])
    }

    if (direction) {
      params.push(['direction', direction])
    }

    const searchParams = new URLSearchParams(params)

    const companyId: string = this.storeProfile.selectedCompany.id

    try {
      const networkResponse = await this.axiosInstance.get<Page<Employee>>(
        `${import.meta.env.VITE_API_BASE_URL}/company/${companyId}/employee/v2`,
        { params: searchParams }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async downloadAllCsv(
    startDate?: string,
    endDate?: string,
    name?: string,
    surname?: string,
    email?: string,
    nif?: string,
    phoneNumber?: string,
    role?: string,
    showInactive?: boolean,
    sort?: string,
    direction?: string
  ): Promise<Blob | ServiceError> {
    this.storeProfile = useProfileStore()
    const locale = localStorage.getItem('appLanguage') || 'es'

    const params: string[][] = []
    if (startDate) {
      params.push(['startDate', startDate])
    }
    if (endDate) {
      params.push(['endDate', endDate])
    }
    if (name) {
      params.push(['name', name])
    }
    if (surname) {
      params.push(['surname', surname])
    }
    if (email) {
      params.push(['email', email])
    }
    if (nif) {
      params.push(['nif', nif])
    }
    if (phoneNumber) {
      params.push(['phoneNumber', phoneNumber])
    }
    if (role) {
      params.push(['role', role])
    }
    if (showInactive !== undefined) {
      params.push(['showInactive', String(showInactive)])
    }
    if (sort) {
      params.push(['sort', sort])
    }
    if (direction) {
      params.push(['direction', direction])
    }
    if (locale) {
      params.push(['locale', locale])
    }


    const searchParams = new URLSearchParams(params)
    const companyId: string = this.storeProfile.selectedCompany.id

    try {
      const networkResponse = await this.axiosInstance.get<Blob>(
        `${import.meta.env.VITE_API_BASE_URL}/company/${companyId}/employee/csv`,
        { params: searchParams, responseType: 'blob' }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async createEmployee(employee: EmployeeRequest): Promise<Employee | ServiceError> {
    this.storeProfile = useProfileStore()

    const companyId: string = this.storeProfile.selectedCompany.id

    try {
      const networkResponse = await this.axiosInstance.post<Employee>(
        `${import.meta.env.VITE_API_BASE_URL}/company/${companyId}/employee`,
        employee
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async updateEmployee(employee: EmployeeRequest): Promise<Employee | ServiceError> {
    this.storeProfile = useProfileStore()

    const companyId: string = this.storeProfile.selectedCompany.id

    try {
      const networkResponse = await this.axiosInstance.put<Employee>(
        `${import.meta.env.VITE_API_BASE_URL}/company/${companyId}/employee/${employee.id}`,
        employee
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async updateEmployeeEmail(employeeId: string, email: string): Promise<Employee | ServiceError> {
    this.storeProfile = useProfileStore()

    const companyId: string = this.storeProfile.selectedCompany.id
    const payload: EmployeeEmailUpdateRequest = { email }

    try {
      const networkResponse = await this.axiosInstance.put<Employee>(
        `${import.meta.env.VITE_API_BASE_URL}/company/${companyId}/employee/${employeeId}/email`,
        payload
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async findEmployeeDeliveryNotes(
    pageNumber?: number,
    pageSize?: number,
    startDate?: string,
    endDate?: string,
    customerIds?: string[],
    sort?: string,
    direction?: string
  ): Promise<Page<Employee> | ServiceError> {
    this.storeProfile = useProfileStore()

    const params: string[][] = []

    if (pageSize) {
      params.push(['pageSize', pageSize.toString()])
    }
    if (pageNumber) {
      params.push(['pageNumber', pageNumber.toString()])
    }

    if (startDate) {
      params.push(['startDate', startDate])
    }

    if (endDate) {
      params.push(['endDate', endDate])
    }

    if (customerIds !== undefined) {
      customerIds.forEach((id) => params.push(['customerId', id]))
    }

    if (sort) {
      params.push(['sort', sort])
    }

    if (direction) {
      params.push(['direction', direction])
    }

    const searchParams = new URLSearchParams(params)

    const companyId: string = this.storeProfile.selectedCompany.id

    try {
      const networkResponse = await this.axiosInstance.get<Page<Employee>>(
        `${import.meta.env.VITE_API_BASE_URL}/company/${companyId}/employee/delivery-notes`,
        { params: searchParams }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async findRoles(): Promise<Role[]> {
    return [Role.ADMIN, Role.DRIVER]
  }

  async findById(id: string): Promise<Employee | ServiceError> {
    this.storeProfile = useProfileStore()

    const companyId: string = this.storeProfile.selectedCompany.id

    try {
      const networkResponse = await this.axiosInstance.get<Employee>(
        `${import.meta.env.VITE_API_BASE_URL}/company/${companyId}/employee/${id}`
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }
}

export const employeeService = new EmployeeService()
